import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import {
    IBlogPost,
    IBlogCategory,
    BlogFilters,
    AdminBlogFilters,
    BlogListResponse,
    BlogDetailResponse,
    SearchResponse,
    CategoryListResponse,
    TagListResponse,
    CreateBlogInput,
    UpdateBlogInput,
    AdminBlogListResponse
} from '@/types/blog'
import { blogApi } from '@/services/api/blog'

interface BlogState {
    // List view state
    posts: IBlogPost[]
    isLoading: boolean
    error: string | null
    pagination: {
        currentPage: number
        totalPages: number
        totalCount: number
        pageSize: number
    }

    // Detail view state
    currentPost: IBlogPost | null
    isLoadingDetail: boolean
    detailError: string | null

    // Admin view state
    adminPosts: IBlogPost[]
    isLoadingAdmin: boolean
    adminError: string | null
    adminPagination: {
        currentPage: number
        totalPages: number
        totalCount: number
        pageSize: number
    }

    // Categories and tags
    categories: IBlogCategory[]
    tags: Array<{ _id: string; count: number }>
    isLoadingCategories: boolean
    isLoadingTags: boolean

    // Search state
    searchResults: IBlogPost[]
    searchQuery: string
    isSearching: boolean

    // Filter state
    activeFilters: BlogFilters
    adminFilters: AdminBlogFilters

    // Featured posts
    featuredPosts: IBlogPost[]
    isLoadingFeatured: boolean

    // Engagement
    likedPosts: string[]
}

const initialState: BlogState = {
    posts: [],
    isLoading: false,
    error: null,
    pagination: {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        pageSize: 10,
    },
    currentPost: null,
    isLoadingDetail: false,
    detailError: null,
    adminPosts: [],
    isLoadingAdmin: false,
    adminError: null,
    adminPagination: {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        pageSize: 20,
    },
    categories: [],
    tags: [],
    isLoadingCategories: false,
    isLoadingTags: false,
    searchResults: [],
    searchQuery: '',
    isSearching: false,
    activeFilters: { page: 1, limit: 10 },
    adminFilters: { page: 1, limit: 20 },
    featuredPosts: [],
    isLoadingFeatured: false,
    likedPosts: [],
}

// ===== ASYNC THUNKS =====

export const fetchPublishedPosts = createAsyncThunk(
    'blog/fetchPublishedPosts',
    async (filters: BlogFilters = {}, { rejectWithValue }) => {
        try {
            const response = await blogApi.getPublishedPosts(filters)
            return response.data
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to fetch blog posts'
            return rejectWithValue(message)
        }
    }
)

export const fetchBlogBySlug = createAsyncThunk(
    'blog/fetchBlogBySlug',
    async (slug: string, { rejectWithValue }) => {
        try {
            const response = await blogApi.getBlogBySlug(slug)
            return response.data
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to fetch blog post'
            return rejectWithValue(message)
        }
    }
)

export const searchBlogs = createAsyncThunk(
    'blog/searchBlogs',
    async (params: { query: string; filters?: BlogFilters }, { rejectWithValue }) => {
        try {
            const response = await blogApi.searchBlogs(params.query, params.filters)
            return response.data
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to search blog posts'
            return rejectWithValue(message)
        }
    }
)

export const fetchByCategory = createAsyncThunk(
    'blog/fetchByCategory',
    async (params: { category: string; filters?: BlogFilters }, { rejectWithValue }) => {
        try {
            const response = await blogApi.getByCategory(params.category, params.filters)
            return response.data
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to fetch blog posts by category'
            return rejectWithValue(message)
        }
    }
)

export const fetchByTag = createAsyncThunk(
    'blog/fetchByTag',
    async (params: { tag: string; filters?: BlogFilters }, { rejectWithValue }) => {
        try {
            const response = await blogApi.getByTag(params.tag, params.filters)
            return response.data
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to fetch blog posts by tag'
            return rejectWithValue(message)
        }
    }
)

export const fetchFeaturedPosts = createAsyncThunk(
    'blog/fetchFeaturedPosts',
    async (limit: number | undefined, { rejectWithValue }) => {
        try {
            const response = await blogApi.getFeaturedPosts(limit)
            return response.data
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to fetch featured posts'
            return rejectWithValue(message)
        }
    }
)

export const fetchCategories = createAsyncThunk(
    'blog/fetchCategories',
    async (_, { rejectWithValue }) => {
        try {
            const response = await blogApi.getCategories()
            return response.data
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to fetch categories'
            return rejectWithValue(message)
        }
    }
)

export const fetchTags = createAsyncThunk(
    'blog/fetchTags',
    async (_, { rejectWithValue }) => {
        try {
            const response = await blogApi.getTags()
            return response.data
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to fetch tags'
            return rejectWithValue(message)
        }
    }
)

export const likeBlog = createAsyncThunk(
    'blog/likeBlog',
    async (postId: string, { rejectWithValue }) => {
        try {
            const response = await blogApi.likeBlog(postId)
            return { postId, likes: response.data.likes }
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to like blog post'
            return rejectWithValue(message)
        }
    }
)

// Admin thunks
export const fetchAdminPosts = createAsyncThunk(
    'blog/fetchAdminPosts',
    async (filters: AdminBlogFilters = {}, { rejectWithValue }) => {
        try {
            const response = await blogApi.getAllPosts(filters)
            return response.data
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to fetch admin blog posts'
            return rejectWithValue(message)
        }
    }
)

export const createBlogPost = createAsyncThunk(
    'blog/createBlogPost',
    async (data: CreateBlogInput, { rejectWithValue }) => {
        try {
            const response = await blogApi.createPost(data)
            return response.data
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to create blog post'
            return rejectWithValue(message)
        }
    }
)

export const updateBlogPost = createAsyncThunk(
    'blog/updateBlogPost',
    async (params: { id: string; data: UpdateBlogInput }, { rejectWithValue }) => {
        try {
            const response = await blogApi.updatePost(params.id, params.data)
            return response.data
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to update blog post'
            return rejectWithValue(message)
        }
    }
)

// ===== SLICE =====

const blogSlice = createSlice({
    name: 'blog',
    initialState,
    reducers: {
        setActiveFilters: (state, action: PayloadAction<BlogFilters>) => {
            state.activeFilters = action.payload
        },
        setAdminFilters: (state, action: PayloadAction<AdminBlogFilters>) => {
            state.adminFilters = action.payload
        },
        clearSearch: (state) => {
            state.searchResults = []
            state.searchQuery = ''
        },
        setCurrentPage: (state, action: PayloadAction<number>) => {
            state.pagination.currentPage = action.payload
        },
        setAdminCurrentPage: (state, action: PayloadAction<number>) => {
            state.adminPagination.currentPage = action.payload
        },
        clearCurrentPost: (state) => {
            state.currentPost = null
            state.detailError = null
        },
    },
    extraReducers: (builder) => {
        // Fetch Published Posts
        builder
            .addCase(fetchPublishedPosts.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchPublishedPosts.fulfilled, (state, action) => {
                state.isLoading = false
                const response = action.payload as BlogListResponse
                state.posts = response.posts
                state.pagination = response.pagination
            })
            .addCase(fetchPublishedPosts.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Fetch Blog by Slug
        builder
            .addCase(fetchBlogBySlug.pending, (state) => {
                state.isLoadingDetail = true
                state.detailError = null
            })
            .addCase(fetchBlogBySlug.fulfilled, (state, action) => {
                state.isLoadingDetail = false
                const response = action.payload as BlogDetailResponse
                state.currentPost = response.post
            })
            .addCase(fetchBlogBySlug.rejected, (state, action) => {
                state.isLoadingDetail = false
                state.detailError = action.payload as string
            })

        // Search Blogs
        builder
            .addCase(searchBlogs.pending, (state) => {
                state.isSearching = true
            })
            .addCase(searchBlogs.fulfilled, (state, action) => {
                state.isSearching = false
                const response = action.payload as SearchResponse
                state.searchResults = response.posts
                state.pagination = response.pagination
            })
            .addCase(searchBlogs.rejected, (state) => {
                state.isSearching = false
            })

        // Fetch by Category
        builder
            .addCase(fetchByCategory.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchByCategory.fulfilled, (state, action) => {
                state.isLoading = false
                const response = action.payload as BlogListResponse
                state.posts = response.posts
                state.pagination = response.pagination
            })
            .addCase(fetchByCategory.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Fetch by Tag
        builder
            .addCase(fetchByTag.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchByTag.fulfilled, (state, action) => {
                state.isLoading = false
                const response = action.payload as BlogListResponse
                state.posts = response.posts
                state.pagination = response.pagination
            })
            .addCase(fetchByTag.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })

        // Fetch Featured Posts
        builder
            .addCase(fetchFeaturedPosts.pending, (state) => {
                state.isLoadingFeatured = true
            })
            .addCase(fetchFeaturedPosts.fulfilled, (state, action) => {
                state.isLoadingFeatured = false
                const response = action.payload as BlogListResponse
                state.featuredPosts = response.posts
            })
            .addCase(fetchFeaturedPosts.rejected, (state) => {
                state.isLoadingFeatured = false
            })

        // Fetch Categories
        builder
            .addCase(fetchCategories.pending, (state) => {
                state.isLoadingCategories = true
            })
            .addCase(fetchCategories.fulfilled, (state, action) => {
                state.isLoadingCategories = false
                const response = action.payload as CategoryListResponse
                state.categories = response.categories || []
            })
            .addCase(fetchCategories.rejected, (state) => {
                state.isLoadingCategories = false
            })

        // Fetch Tags
        builder
            .addCase(fetchTags.pending, (state) => {
                state.isLoadingTags = true
            })
            .addCase(fetchTags.fulfilled, (state, action) => {
                state.isLoadingTags = false
                const response = action.payload as TagListResponse
                state.tags = response.tags
            })
            .addCase(fetchTags.rejected, (state) => {
                state.isLoadingTags = false
            })

        // Like Blog
        builder
            .addCase(likeBlog.fulfilled, (state, action) => {
                const { postId, likes } = action.payload
                if (!state.likedPosts.includes(postId)) {
                    state.likedPosts.push(postId)
                }
                if (state.currentPost && state.currentPost._id === postId) {
                    state.currentPost.likes = likes
                }
                // Update post in list
                const post = state.posts.find((p) => p._id === postId)
                if (post) {
                    post.likes = likes
                }
            })

        // Fetch Admin Posts
        builder
            .addCase(fetchAdminPosts.pending, (state) => {
                state.isLoadingAdmin = true
                state.adminError = null
            })
            .addCase(fetchAdminPosts.fulfilled, (state, action) => {
                state.isLoadingAdmin = false
                const response = action.payload as AdminBlogListResponse
                state.adminPosts = response.posts
                state.adminPagination = response.pagination
            })
            .addCase(fetchAdminPosts.rejected, (state, action) => {
                state.isLoadingAdmin = false
                state.adminError = action.payload as string
            })

        // Create Blog Post
        builder
            .addCase(createBlogPost.fulfilled, (state, action) => {
                const response = action.payload as BlogDetailResponse
                state.adminPosts.unshift(response.post)
            })

        // Update Blog Post
        builder
            .addCase(updateBlogPost.fulfilled, (state, action) => {
                const response = action.payload as BlogDetailResponse
                const index = state.adminPosts.findIndex((p) => p._id === response.post._id)
                if (index !== -1) {
                    state.adminPosts[index] = response.post
                }
                if (state.currentPost && state.currentPost._id === response.post._id) {
                    state.currentPost = response.post
                }
            })
    },
})

export const {
    setActiveFilters,
    setAdminFilters,
    clearSearch,
    setCurrentPage,
    setAdminCurrentPage,
    clearCurrentPost,
} = blogSlice.actions

export default blogSlice.reducer